import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IconPlus, IconAlertTriangle, IconChevronRight } from "@tabler/icons-react";
import "./dashboard.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import NewAppointmentModal from "../../components/NewAppointmentModal/NewAppointmentModal";
import { addDays, formatDateShort, formatRM, formatTime, initials } from "../../utils/format";
import { FEATURES } from "../../config/features";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";

// The next status a front-desk user can move an appointment to.
export const NEXT_STEP = {
  scheduled: { to: "confirmed", label: "Confirm" },
  confirmed: { to: "checked-in", label: "Check in" },
  "checked-in": { to: "in-treatment", label: "Start treatment" },
  "in-treatment": { to: "completed", label: "Complete" },
};

const DashboardPage = () => {
  const {
    today,
    appointments,
    invoices,
    activeDentists,
    dentistById,
    patientById,
    apptLabel,
    invoiceTotal,
    invoiceStatus,
    updateAppointment,
  } = useClinicData();
  const [showNew, setShowNew] = useState(false);

  const todays = useMemo(
    () => appointments.filter((a) => a.date === today).sort((x, y) => x.start.localeCompare(y.start)),
    [appointments, today]
  );

  const active = todays.filter((a) => a.status !== "cancelled" && a.status !== "no-show");
  const waiting = todays.filter((a) => a.status === "checked-in");
  const completed = todays.filter((a) => a.status === "completed");
  const collectedToday = invoices
    .filter((inv) => inv.date === today)
    .reduce((sum, inv) => sum + Math.min(inv.paid, invoiceTotal(inv)), 0);

  const tomorrow = addDays(today, 1);
  const unconfirmedTomorrow = appointments.filter((a) => a.date === tomorrow && a.status === "scheduled");
  const overdue = invoices.filter((inv) => invoiceStatus(inv) === "overdue");
  const overdueAmount = overdue.reduce((sum, inv) => sum + invoiceTotal(inv) - inv.paid, 0);
  const noShows = todays.filter((a) => a.status === "no-show");

  const alertPatients = [...new Set(active.map((a) => a.patientId))]
    .map((id) => patientById[id])
    .filter((p) => p && p.alerts.length);

  const dentistStatus = activeDentists.map((dentist) => {
    const mine = active.filter((a) => a.dentistId === dentist.id);
    const current = mine.find((a) => a.status === "in-treatment");
    const queued = mine.find((a) => a.status === "checked-in");
    const next = mine.find((a) => a.status === "scheduled" || a.status === "confirmed");
    return { dentist, current, queued, next };
  });

  const stats = [
    { label: "Booked today", value: active.length },
    { label: "In the waiting room", value: waiting.length },
    { label: "Completed", value: completed.length },
    { label: "Collected today", value: formatRM(collectedToday) },
  ];

  return (
    <div className="dashboard-page">
      <div className="toolbar">
        <div>
          <h2 className="dashboard-page__greeting">Good morning</h2>
          <p className="dashboard-page__lead">
            {active.length} patients booked with {activeDentists.length} dentists today.
          </p>
        </div>
        <button className="btn btn--primary" type="button" onClick={() => setShowNew(true)}>
          <IconPlus size={18} stroke={2} />
          New appointment
        </button>
      </div>

      <section className="dashboard-page__stats" aria-label="Today at a glance">
        {stats.map((stat) => (
          <div className="dashboard-page__stat" key={stat.label}>
            <span className="dashboard-page__stat-label">{stat.label}</span>
            <strong className="dashboard-page__stat-value">{stat.value}</strong>
          </div>
        ))}
      </section>

      <section className="dashboard-page__now" aria-labelledby="now-title">
        <h3 className="dashboard-page__section-title" id="now-title">
          Dentists right now
        </h3>
        <div className="dashboard-page__now-grid">
          {dentistStatus.map(({ dentist, current, queued, next }) => {
            const state = current ? "busy" : queued ? "ready" : "free";
            const focus = current || queued;
            return (
              <article className={`dentist-status dentist-status--${state}`} key={dentist.id}>
                <header className="dentist-status__header">
                  <span>
                    {state === "busy" ? "In treatment" : state === "ready" ? "Patient waiting" : "Free"}
                  </span>
                </header>
                <div className="dentist-status__dentist">
                  <span className="avatar" style={{ background: dentist.color, color: "#fff" }}>
                    {dentist.initials}
                  </span>
                  <span>
                    <strong>{dentist.name}</strong>
                    <span className="dentist-status__role">{dentist.role}</span>
                  </span>
                </div>
                <div className="dentist-status__body">
                  {focus ? (
                    <>
                      <strong>{patientById[focus.patientId]?.name}</strong>
                      <span>
                        {apptLabel(focus)}, booked {formatTime(focus.start)}
                      </span>
                    </>
                  ) : (
                    <span>No patient with this dentist.</span>
                  )}
                </div>
                <footer className="dentist-status__next">
                  {next ? (
                    <>
                      Next: <strong>{patientById[next.patientId]?.name}</strong> at {formatTime(next.start)}
                    </>
                  ) : (
                    "No more bookings today"
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      </section>

      <div className="dashboard-page__grid">
        <section className="panel panel--flush" aria-labelledby="schedule-title">
          <div className="panel__header dashboard-page__panel-head">
            <div>
              <h3 className="panel__title" id="schedule-title">
                Today's schedule
              </h3>
              <p className="panel__subtitle">{formatDateShort(today)}</p>
            </div>
            <Link className="btn btn--secondary btn--sm" to={`${BASE_PATH}/appointments`}>
              Open calendar
            </Link>
          </div>

          <ul className="schedule-list">
            {todays.map((appt) => {
              const patient = patientById[appt.patientId];
              const dentist = dentistById[appt.dentistId];
              const step = NEXT_STEP[appt.status];
              return (
                <li className="schedule-list__item" key={appt.id}>
                  <span className="schedule-list__time">{formatTime(appt.start)}</span>
                  <span className="schedule-list__bar" style={{ background: dentist.color }} aria-hidden="true" />
                  <div className="schedule-list__info">
                    <Link className="schedule-list__patient" to={`${BASE_PATH}/patients/${patient.id}`}>
                      {patient.name}
                    </Link>
                    <span className="schedule-list__meta">
                      {apptLabel(appt)}, {dentist.name}
                    </span>
                  </div>
                  <StatusBadge status={appt.status} />
                  <div className="schedule-list__action">
                    {appt.status === "completed" && !appt.checkedOut && (
                      <Link className="btn btn--primary btn--sm" to={`${BASE_PATH}/checkout?appt=${appt.id}`}>
                        Check out
                      </Link>
                    )}
                    {step && (
                      <button
                        className="btn btn--secondary btn--sm"
                        type="button"
                        onClick={() => updateAppointment(appt.id, { status: step.to })}
                      >
                        {step.label}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="panel" aria-labelledby="attention-title">
          <div className="panel__header">
            <h3 className="panel__title" id="attention-title">
              Needs attention
            </h3>
          </div>

          <div className="attention-list">
            <Link className="attention-list__item" to={`${BASE_PATH}/appointments?date=${tomorrow}`}>
              <span className="attention-list__icon attention-list__icon--warning">
                <IconAlertTriangle size={18} stroke={1.75} />
              </span>
              <span className="attention-list__text">
                <strong>{unconfirmedTomorrow.length} unconfirmed for tomorrow</strong>
                <span>Call or message to confirm</span>
              </span>
              <IconChevronRight size={18} stroke={1.75} />
            </Link>
            {FEATURES.billing && (
              <Link className="attention-list__item" to={`${BASE_PATH}/billing`}>
                <span className="attention-list__icon attention-list__icon--danger">
                  <IconAlertTriangle size={18} stroke={1.75} />
                </span>
                <span className="attention-list__text">
                  <strong>{overdue.length} overdue invoices</strong>
                  <span>{formatRM(overdueAmount)} outstanding</span>
                </span>
                <IconChevronRight size={18} stroke={1.75} />
              </Link>
            )}
            <Link className="attention-list__item" to={`${BASE_PATH}/appointments`}>
              <span className="attention-list__icon attention-list__icon--danger">
                <IconAlertTriangle size={18} stroke={1.75} />
              </span>
              <span className="attention-list__text">
                <strong>{noShows.length} no-show today</strong>
                <span>
                  {noShows.length
                    ? noShows.map((a) => patientById[a.patientId]?.name).join(", ")
                    : "Nobody has missed their slot"}
                </span>
              </span>
              <IconChevronRight size={18} stroke={1.75} />
            </Link>
          </div>

          <h4 className="dashboard-page__subhead">Medical alerts for today's patients</h4>
          {alertPatients.length ? (
            <ul className="alert-list">
              {alertPatients.map((p) => (
                <li className="alert-list__item" key={p.id}>
                  <span className="avatar">{initials(p.name)}</span>
                  <span>
                    <Link className="alert-list__name" to={`${BASE_PATH}/patients/${p.id}`}>
                      {p.name}
                    </Link>
                    <span className="alert-list__tags">
                      {p.alerts.map((alert) => (
                        <span className="badge badge--danger" key={alert}>
                          {alert}
                        </span>
                      ))}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="dashboard-page__muted">No medical alerts for today's patients.</p>
          )}
        </section>
      </div>

      {showNew && <NewAppointmentModal onClose={() => setShowNew(false)} />}
    </div>
  );
};

export default DashboardPage;
